/* Quiet Spark: product page behaviour (sections/main-product.liquid).
   <q-product>        variant state, prices, button states, ?variant= URL, add to cart via QS.cart.add,
                      inline errors, sticky add-to-cart bar, panel scroll fade, gallery counter.
   <q-product-facts>  rotating fact with dot pagination (pauses on hover/focus, static with reduced motion).
   Needs assets/theme.js (window.QS). Without it, the product form posts to /cart/add as normal. */
(() => {
  'use strict';

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const media = (query) => window.matchMedia(query);
  const DESKTOP = '(min-width: 990px)';
  const REDUCED = '(prefers-reduced-motion: reduce)';
  const str = (key, fallback) => (window.QS && window.QS.strings && window.QS.strings[key]) || fallback;
  const listen = (mql, fn) => (mql.addEventListener ? mql.addEventListener('change', fn) : mql.addListener(fn));
  const unlisten = (mql, fn) => (mql.removeEventListener ? mql.removeEventListener('change', fn) : mql.removeListener(fn));

  /* ---------- Product ---------- */
  define('q-product', class extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      try { this.data = JSON.parse(json ? json.textContent : '{}'); } catch (e) { this.data = {}; }
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.form = this.querySelector('form[data-product-form]');
      this.groups = [...this.querySelectorAll('[data-option-group]')];
      this.mainButton = this.querySelector('[data-main-add]');
      this.bar = this.querySelector('[data-sticky-bar]');
      this.panel = this.querySelector('[data-product-panel]');
      this.track = this.querySelector('[data-gallery-track]');
      this.counter = this.querySelector('[data-gallery-index]');
      this.desktop = media(DESKTOP);

      this.onChange = (e) => { if (e.target.matches('[data-option-input]')) this.select(this.resolve(), true); };
      this.onSubmit = (e) => this.submit(e);
      this.addEventListener('change', this.onChange);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      // Follow the checked pills (browsers may restore them on reload); fall back to Liquid's selection.
      this.current = (this.groups.length && this.resolve()) || this.byId(this.data.current);
      const urlVariant = this.byId(new URLSearchParams(window.location.search).get('variant'));
      if (urlVariant && urlVariant !== this.current) { this.current = urlVariant; this.checkInputs(urlVariant); }
      this.render(false);

      this.setupBar();
      this.setupPanel();
      this.setupGallery();
      this.setupEditor();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.barObserver) this.barObserver.disconnect();
      if (this.onBreakpoint) unlisten(this.desktop, this.onBreakpoint);
      if (this.panelObserver) this.panelObserver.disconnect();
      if (this.panel && this.onPanelScroll) this.panel.removeEventListener('scroll', this.onPanelScroll);
      if (this.onLoad) window.removeEventListener('load', this.onLoad);
      if (this.onGalleryLoad) window.removeEventListener('load', this.onGalleryLoad);
      clearTimeout(this.galleryTimer);
      if (this.track && this.onTrackScroll) this.track.removeEventListener('scroll', this.onTrackScroll);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      cancelAnimationFrame(this.frame);
      clearTimeout(this.flashTimer);
    }

    /* Variants */
    byId(id) {
      const n = Number(id);
      return n ? this.variants.find((v) => v.id === n) || null : null;
    }
    position(group) { return Math.max(0, (Number(group.dataset.position) || 1) - 1); }
    selectedOptions() {
      const selected = [];
      this.groups.forEach((group) => {
        const checked = group.querySelector('[data-option-input]:checked');
        selected[this.position(group)] = checked ? checked.value : null;
      });
      return selected;
    }
    resolve() {
      if (!this.groups.length) return this.byId(this.data.current) || this.variants[0] || null;
      const selected = this.selectedOptions();
      return this.variants.find((v) => selected.every((value, i) => value == null || v.options[i] === value)) || null;
    }
    checkInputs(variant) {
      this.groups.forEach((group) => {
        const value = variant.options[this.position(group)];
        group.querySelectorAll('[data-option-input]').forEach((input) => { input.checked = input.value === value; });
      });
    }
    labelFor(variant) {
      if (!variant) return str('unavailable', 'Unavailable');
      return variant.available ? str('addToCart', 'Add to cart') : str('soldOut', 'Sold out');
    }

    select(variant, fromUser) {
      this.current = variant;
      this.render(fromUser);
    }

    render(fromUser) {
      const v = this.current;
      const available = Boolean(v && v.available);

      // The form's single name="id" field (variant select, or hidden input) feeds both the native
      // POST fallback and Shopify's dynamic checkout button, which listens for change events.
      const idField = this.form && this.form.elements.namedItem('id');
      if (idField && v && 'value' in idField && idField.value !== String(v.id)) {
        idField.value = String(v.id);
        idField.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.groups.forEach((group) => {
        const out = group.querySelector('[data-option-current]');
        const checked = group.querySelector('[data-option-input]:checked');
        if (out) out.textContent = checked ? checked.value : '';
      });

      if (v) {
        this.querySelectorAll('[data-product-price]').forEach((el) => { if (el.textContent !== v.price) el.textContent = v.price; });
        this.querySelectorAll('[data-variant-title]').forEach((el) => { el.textContent = v.title; });
      }

      if (!this.busy) this.paintButtons();
      this.paintAvailability();

      if (!fromUser) return;
      this.clearError();
      const status = this.querySelector('[data-product-status]');
      if (status) status.textContent = available ? '' : this.labelFor(v);
      if (v) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (e) { /* history unavailable: ignore */ }
        if (v.media) this.showMedia(v.media);
      }
      this.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail: { variant: v, sectionId: this.dataset.section } }));
    }

    paintButtons(label) {
      const v = this.current;
      const available = Boolean(v && v.available);
      this.querySelectorAll('[data-add-button]').forEach((btn) => {
        btn.disabled = !available;
        const text = btn.querySelector('[data-add-label]');
        if (text) text.textContent = label || this.labelFor(v);
        const price = btn.querySelector('[data-add-price]');
        if (price) price.hidden = !available || Boolean(label);
      });
    }

    paintAvailability() {
      const selected = this.selectedOptions();
      this.groups.forEach((group) => {
        const pos = this.position(group);
        group.querySelectorAll('[data-option-input]').forEach((input) => {
          const ok = this.variants.some((v) => v.available && v.options[pos] === input.value
            && selected.every((value, i) => i === pos || value == null || v.options[i] === value));
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !ok);
          const hint = label.querySelector('[data-sold-out-hint]');
          if (hint) hint.hidden = ok;
        });
      });
    }

    /* Add to cart */
    async submit(event) {
      const QS = window.QS;
      if (!QS || !QS.cart || typeof QS.cart.add !== 'function' || !window.fetch) return; // native POST to /cart/add
      event.preventDefault();
      const v = this.current;
      if (this.busy || !v || !v.available) return;

      const qtyField = this.form.elements.namedItem('quantity');
      const quantity = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);

      this.clearError();
      this.setBusy(true);
      try {
        await QS.cart.add([{ id: v.id, quantity }]);
        this.setBusy(false);
        if (QS.settings && QS.settings.cartAutoOpen === false) this.flashAdded();
      } catch (err) {
        this.setBusy(false);
        const data = err && err.data;
        const message = data && typeof data.description === 'string' && data.description
          ? data.description
          : str('cartError', 'Something went wrong. Please refresh the page and try again.');
        this.showError(message);
      }
    }

    setBusy(on) {
      this.busy = on;
      this.querySelectorAll('[data-add-button]').forEach((btn) => {
        btn.classList.toggle('is-loading', on);
        if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
      });
      this.paintButtons(on ? str('adding', 'Adding') : null);
    }

    flashAdded() {
      const added = this.dataset.added;
      if (!added) return;
      this.paintButtons(added);
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { if (!this.busy) this.paintButtons(); }, 2200);
    }

    showError(message) {
      const barVisible = Boolean(this.bar && this.bar.classList.contains('is-visible'));
      this.querySelectorAll('[data-product-error]').forEach((el) => {
        if (el.closest('[data-sticky-bar]') && !barVisible) return;
        el.textContent = message;
      });
    }

    clearError() {
      this.querySelectorAll('[data-product-error]').forEach((el) => { el.textContent = ''; });
    }

    /* Sticky add-to-cart bar: shows whenever the main button is out of view, including on arrival (phones/tablets) */
    setupBar() {
      if (!this.bar || !this.mainButton || !('IntersectionObserver' in window)) return;
      const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0;
      this.buttonInView = true;
      this.footerInView = false;
      this.footer = document.querySelector('.footer') || document.querySelector('footer');
      this.barObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.mainButton) {
            this.buttonInView = entry.isIntersecting;
          } else {
            this.footerInView = entry.isIntersecting;
          }
        });
        this.toggleBar();
      }, { rootMargin: `-${Math.round(headerH)}px 0px 0px 0px` });
      this.barObserver.observe(this.mainButton);
      if (this.footer) this.barObserver.observe(this.footer);
      this.onBreakpoint = () => { this.toggleBar(); if (this.desktop.matches && this.hydrateGallery) this.hydrateGallery(); };
      listen(this.desktop, this.onBreakpoint);
    }

    toggleBar() {
      const show = !this.buttonInView && !this.footerInView && !this.desktop.matches;
      this.bar.classList.toggle('is-visible', show);
      this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (!show) {
        const err = this.bar.querySelector('[data-product-error]');
        if (err) err.textContent = '';
      }
    }

    /* Desktop panel: soft bottom fade while its content can still scroll */
    setupPanel() {
      if (!this.panel) return;
      this.onPanelScroll = () => {
        const p = this.panel;
        const scrollable = p.scrollHeight - p.clientHeight > 4;
        p.classList.toggle('is-scrollable', scrollable);
        p.classList.toggle('is-end', !scrollable || p.scrollTop + p.clientHeight >= p.scrollHeight - 4);
      };
      this.panel.addEventListener('scroll', this.onPanelScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.panelObserver = new ResizeObserver(this.onPanelScroll);
        this.panelObserver.observe(this.panel);
        [...this.panel.children].forEach((child) => this.panelObserver.observe(child));
      }
      this.onLoad = this.onPanelScroll;
      window.addEventListener('load', this.onLoad);
      this.onPanelScroll();
    }

    /* Phone/tablet gallery counter */
    setupGallery() {
      if (!this.track) return;
      // Slides after the first are deferred (snippets/product-gallery.liquid). Load them on the first swipe or
      // arrow tap, about 1.5 s after the page has loaded, or at once on desktop where they stack.
      this.hydrateGallery = () => {
        if (this.galleryHydrated) return;
        this.galleryHydrated = true;
        if (window.QS && QS.hydrate) QS.hydrate(this.track);
      };
      if (this.desktop.matches) this.hydrateGallery();
      this.track.addEventListener('pointerdown', this.hydrateGallery, { once: true, passive: true });
      this.onGalleryLoad = () => { this.galleryTimer = setTimeout(this.hydrateGallery, 1500); };
      if (document.readyState === 'complete') this.onGalleryLoad();
      else window.addEventListener('load', this.onGalleryLoad, { once: true });
      this.onTrackScroll = () => {
        this.hydrateGallery();
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          const first = this.track.firstElementChild;
          if (!first || !this.counter) return;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const step = first.getBoundingClientRect().width + gap || 1;
          const count = this.track.children.length;
          const index = Math.min(count - 1, Math.max(0, Math.round(this.track.scrollLeft / step)));
          this.counter.textContent = String(index + 1);
        });
      };
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    }

    showMedia(id) {
      if (!this.track || this.desktop.matches) return;
      const slide = this.track.querySelector(`[data-media-id="${id}"]`);
      if (!slide) return;
      const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: media(REDUCED).matches ? 'auto' : 'smooth' });
    }

    /* Theme editor: selecting a tab block opens its accordion on phone previews */
    setupEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.onBlockSelect = (e) => {
        if (!this.contains(e.target) || this.desktop.matches) return;
        const id = e.detail && e.detail.blockId;
        const item = id && this.querySelector(`[data-accordion-block="${id}"]`);
        if (item) item.open = true;
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  });

  /* ---------- Rotating fact ---------- */
  define('q-product-facts', class extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-fact]')];
      this.dots = [...this.querySelectorAll('[data-fact-dot]')];
      if (this.items.length < 2) return;
      this.index = Math.max(0, this.items.findIndex((el) => el.classList.contains('is-active')));
      this.interval = Math.max(2000, Number(this.dataset.interval) || 4500);
      this.holds = new Set();
      this.inView = true;
      this.reduced = media(REDUCED);

      this.onEnter = () => this.hold('hover', true);
      this.onLeave = () => this.hold('hover', false);
      this.onFocusIn = () => this.hold('focus', true);
      this.onFocusOut = (e) => { if (!this.contains(e.relatedTarget)) this.hold('focus', false); };
      this.onClick = (e) => {
        const dot = e.target.closest('[data-fact-dot]');
        if (dot) { this.go(this.dots.indexOf(dot)); this.schedule(); }
      };
      this.onVisibility = () => this.schedule();
      this.onMotion = () => this.schedule();
      this.onSelect = (e) => {
        const i = this.items.indexOf(e.target.closest('[data-fact]'));
        if (i > -1) { this.go(i); this.hold('editor', true); }
      };
      this.onDeselect = (e) => { if (this.items.includes(e.target.closest('[data-fact]'))) this.hold('editor', false); };

      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('click', this.onClick);
      this.addEventListener('shopify:block:select', this.onSelect);
      this.addEventListener('shopify:block:deselect', this.onDeselect);
      document.addEventListener('visibilitychange', this.onVisibility);
      listen(this.reduced, this.onMotion);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          this.schedule();
        });
        this.observer.observe(this);
      }
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (!this.items || this.items.length < 2) return;
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('shopify:block:select', this.onSelect);
      this.removeEventListener('shopify:block:deselect', this.onDeselect);
      document.removeEventListener('visibilitychange', this.onVisibility);
      unlisten(this.reduced, this.onMotion);
      if (this.observer) this.observer.disconnect();
    }

    hold(reason, on) {
      if (on) this.holds.add(reason); else this.holds.delete(reason);
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.holds.size || !this.inView || document.hidden || this.reduced.matches) return;
      this.timer = setTimeout(() => { this.go(this.index + 1); this.schedule(); }, this.interval);
    }

    go(i) {
      const n = this.items.length;
      this.index = ((i % n) + n) % n;
      this.items.forEach((el, k) => {
        const on = k === this.index;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      this.dots.forEach((dot, k) => dot.setAttribute('aria-current', k === this.index ? 'true' : 'false'));
    }
  });
})();
